import { v4 as uuidv4 } from "uuid";
import type { TableState, TableStateFunctions } from "@/types/tableState.types";
import type { VivoPlotProps } from "@/components/VivoPlots/types";

/**
 * Plot CRUD handlers for a dashboard: update, hide, delete, duplicate, and
 * patch the config of a plot within the current table state. Each handler
 * derives the next plot list and hands it to `tableStateFunctions.setPlots`.
 */
export const usePlotFunctions = (
  currentTableState: TableState,
  tableStateFunctions: TableStateFunctions,
) => {
  const plots = (currentTableState.plots || []) as VivoPlotProps[];

  const updatePlotById = (key: string, newPlotInfo: VivoPlotProps) => {
    tableStateFunctions.setPlots(
      plots.map((plot) => {
        if (plot.config.plotId === key) {
          return newPlotInfo;
        }
        return plot;
      }),
    );
  };

  const onHidePlot = (plotId: string) => {
    tableStateFunctions.setPlots(
      plots.map((plot) => {
        if (plot.config.plotId === plotId) {
          return { ...plot, config: { ...plot.config, hidden: true } };
        }
        return plot;
      }),
    );
  };

  const onDeletePlot = (plotId: string) => {
    tableStateFunctions.setPlots(
      plots.filter((plot) => plot.config.plotId !== plotId),
    );
  };

  const onDuplicatePlot = (plotId: string) => {
    const index = plots.findIndex((plot) => plot.config.plotId === plotId);
    if (index === -1) return;
    const original = plots[index];
    const newPlotId = uuidv4();
    const cloned: VivoPlotProps = {
      ...original,
      config: {
        ...original.config,
        plotId: newPlotId,
        plotClassName: newPlotId,
      },
    };
    const updated = [...plots];
    updated.splice(index + 1, 0, cloned);
    tableStateFunctions.setPlots(updated);
  };

  const onConfigChange = (
    plotId: string,
    updates: Partial<VivoPlotProps["config"]>,
  ) => {
    tableStateFunctions.setPlots(
      plots.map((plot) => {
        if (plot.config.plotId === plotId) {
          return { ...plot, config: { ...plot.config, ...updates } };
        }
        return plot;
      }),
    );
  };

  return {
    updatePlotById,
    onHidePlot,
    onDeletePlot,
    onDuplicatePlot,
    onConfigChange,
  };
};
